import { Product } from '../../lib/products';

type DiscountTypeType = 'fixed' | 'percentage';
export interface BuildABoxSetting {
  id: string;
  boxSize: string;
  boxMinSize?: string;
  boxMaxSize?: string;
  label: string;
  pricePerItem?: string;
  pricePerBox: string;
  discountAmount?: number;
  discountType?: DiscountTypeType;
  discountId?: string;
  discountCode?: string;
  sellingPlanGroups?: string[];
}

export enum BundleTypes {
  classic = 'classic',
  buildABox = 'buildABox',
  dynamic = 'dynamicBaB',
  shuffle = 'shuffle',
  fixed = 'fixed',
}

export enum VariantStyle {
  collapse = 'collapse',
  split = 'split',
}

export interface BasicCollection {
  id: string;
  handle: string;
}

export interface FullCollection extends BasicCollection {
  title: string;
  productsCount: {
    count: number;
    precision: 'EXACT' | 'AT_LEAST';
  };
  image?: {
    originalSrc: string;
  };
}

export type Collection = BasicCollection | FullCollection;

export interface BasicVariant {
  id: string;
  title: string;
  productType?: string;
  price?: string;
  sectionId?: string;
  image?: {
    url: string;
  };
}

export interface BasicProduct {
  id: string;
  title?: string;
  handle?: string;
  variants: BasicVariant[];
  hasOnlyDefaultVariant?: boolean;
  totalInventory?: number;
  status?: string;
  loaded?: boolean;
  productType?: string;
  sectionId?: string;
  images?: {
    originalSrc?: string;
    id?: string;
  }[];
}

export type ShuffleProduct = BasicProduct | Product;

export interface OriginalVariant {
  productId?: string;
  variantId: string;
}

export interface Combination {
  id?: string;
  title: string;
  options: string[];
  price?: string;
  originalVariants?: OriginalVariant[];
}

export enum ShufflePricingStrategy {
  fixed = 'fixed',
  variantSpecific = 'variant-specific',
}

export enum BaBProductType {
  productVariant = 'productVariant',
  collection = 'collection',
}

export enum BaBRuleType {
  fixed = 'fixed',
  minMax = 'minMax',
}

export interface ShuffleSettings {
  quantityMultiplier?: number;
  type: ShufflePricingStrategy;
  options: string[];
  variantPrices: Combination[];
  shouldExpire?: boolean;
}

export interface PercentageType {
  percentage: number;
}

export interface AmountType {
  discountAmount: {
    amount: number;
    appliesOnEachItem: boolean;
  };
}

export type DiscountValue = PercentageType | AmountType;
export type DiscountType = {
  title: string;
  code: string;
  startsAt: string;
  customerGets: {
    value: DiscountValue;
    items: {
      collections?: {
        add: string[];
      };
      products?: {
        productsToAdd: string[];
        productVariantsToAdd: string[];
      };
    };
  };
  minimumRequirement: {
    quantity: {
      greaterThanOrEqualToQuantity: string;
    };
  };
  combinesWith?: {
    orderDiscounts: boolean;
    productDiscounts: boolean;
    shippingDiscounts: boolean;
  };
};
export interface Section {
  id: string;
  name: string;
  products?: ShuffleProduct[];
  collections?: Collection[];
  settings?: BuildABoxSettingDynamo;
}

export enum LimitTypesEnum {
  MINIMUM = 'MINIMUM',
  MAXIMUM = 'MAXIMUM',
  BOTH = 'BOTH',
}

export interface SectionOverride {
  sectionLimit: {
    limitType: LimitTypesEnum;
    limitValueMax?: number;
    limitValueMin?: number;
    limitLabel: string;
    enabled: boolean;
  };
  sizeAndSection: string;
  sectionId: string;
}

export interface Bundle {
  id: string;
  active: boolean;
  name: string;
  handle: string;
  type: BundleTypes;
  buildABoxSettings: BuildABoxSetting[];
  collections: Collection[];
  addOns: Collection[];
  productId?: string;
  products: ShuffleProduct[];
  babProducts?: ShuffleProduct[];
  shuffleSettings?: ShuffleSettings;
  fixedPrice?: string;
  hasSections?: boolean;
  buildABoxSettingsType?: 'fixed' | 'minMax';
  babProductType?: BaBProductType;
  variantStyle?: VariantStyle;
  babExperience?: 'dynamicBaB-experience' | 'buildABox-experience';
  bundleLoadAutoswap?: boolean;
  hasBuildABoxFiltersActive?: boolean;
  buildABoxFilterGroups?: BuildABoxFilterGroup[];
  sections?: Section[];
  hasSectionOverrides?: boolean;
  sectionOverrides?: SectionOverride[];
  transformedFromLegacy?: boolean;
  variantsToAdd?: { id: string; quantity: number }[];
  triggerVariant?: string;
  sellingPlanGroups?: {
    id: string;
    title: string;
    sellingPlanGroups: {
      id: string;
      checked: boolean;
      name: string;
      sellingPlans: { id: string; name: string }[];
    }[];
  }[];
  updatedAt?: string;
}

export interface BundleResponse {
  bundle: Bundle;
  wasABaBRemoved: boolean;
}

export interface BuildABoxSettingDynamo {
  VariantId: string;
  Label: string;
  Price: string;
  Size: string;
  MinSize?: string;
  MaxSize?: string;
  DynamicDiscountType?: DiscountTypeType;
  DynamicDiscountAmount?: number;
  DynamicDiscountCode?: string;
  DynamicDiscountId?: string;
}

export interface CollectionDynamo {
  Id: string;
  Handle: string;
}

export interface ProductDynamo {
  Id: string;
  Title: string;
  ProductType?: string;
  Variants: { VariantId: string; Name: string }[];
}

export interface DynamoShuffleSettings {
  QuantityMultiplier?: number;
  ShouldExpire?: boolean;
  VariantId?: string;
  PriceType: string;
  Options?: string[];
  VariantPrices?: {
    VariantId?: string;
    Label: string;
    Price?: string;
    OriginalVariants: {
      ProductId: string;
      VariantId: string;
    }[];
  }[];
}

// Order Update (aka FixedBundle)
export interface OrderUpdateDynamo {
  Name: string;
  PK: string;
  SK: string;
  TriggerVariant: string;
  VariantsToAdd: string[];
}
export interface BundleDynamo {
  Active: boolean;
  HasSections: boolean;
  VariantStyle: VariantStyle;
  AddonsCollections: CollectionDynamo[];
  Collections: CollectionDynamo[];
  Products: ProductDynamo[];
  Name: string;
  Handle: string;
  PK: string;
  ProductId: string;
  SK: string;
  Settings: BuildABoxSettingDynamo[];
  Type: string;
  ShuffleSettings?: DynamoShuffleSettings;
  BundleLoadAutoswap?: boolean;
  HasBuildABoxFiltersActive?: boolean;
  BuildABoxFilterGroups?: DynamoBuildABoxFilterGroup[];
  Sections?: DynamoSection[];
  UpdatedAt?: string;
}

export interface DynamoSection {
  Id: string;
  Name: string;
  Products?: ProductDynamo[];
  Collections?: CollectionDynamo[];
  Settings?: BuildABoxSettingDynamo[];
}

export enum BuildABoxFilterType {
  productTag = 'productTag',
  variantTitle = 'variantTitle',
}

export enum BuildABoxFilterCondition {
  isEqual = 'isEqual',
  isNotEqual = 'isNotEqual',
  contains = 'contains',
  notContains = 'notContains',
}

export enum FilterConditional {
  AND = 'AND',
  OR = 'OR',
}

export interface BuildABoxFilter {
  id: string;
  name: string;
  filterType: BuildABoxFilterType;
  filterCondition: BuildABoxFilterCondition;
  filterValue: string;
}
export interface BuildABoxFilterGroup {
  id: string;
  groupName: string;
  filterConditional: FilterConditional;
  isFilterComplete: boolean;
  filters: BuildABoxFilter[];
}

export interface DynamoBuildABoxFilterGroup {
  Id: string;
  GroupName: string;
  IsFilterComplete: boolean;
  FilterConditional: string;
  Filters?: DynamoBuildABoxFilter[];
}

export interface DynamoBuildABoxFilter {
  Id: string;
  Name: string;
  FilterType: string;
  FilterCondition: string;
  FilterValue: string;
}

export const isFullCollection = (
  collection: Collection,
): collection is FullCollection =>
  (collection as FullCollection)?.title !== undefined;

export const getCollectionId = (collection: Collection): string =>
  collection.id;

export const getCollectionsIds = (collections: Collection[]): string[] =>
  collections.map(getCollectionId);

export const isProduct = (product: ShuffleProduct): product is Product =>
  (product as Product).title !== undefined;

export interface ApiOrderUpdate {
  name: string;
  variantGIdsToAdd: { id: string; quantity: number }[];
  triggerVariantGId: string;
}

export interface FormOrderUpdate {
  name: string;
  variantsToAdd: any[];
  triggerVariant: any;
}
